import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
import { apiSignIn, apiSignOut, apiSignUp } from "services/AuthService";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { resetHomeDecisions } from "utils/homeDecisions";
import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";
import { hasServerAnswer } from "services/hasServerAnswer";
import { safeRedirectPath } from "utils/safeRedirect";

export const SIGN_IN_BAD_CREDENTIALS_MESSAGE =
  "That email and password don't match. Check them and try again.";
export const SIGN_IN_UNREACHABLE_MESSAGE =
  "We couldn't reach the server. Check your connection and try again.";

export const SIGN_IN_NO_CONNECTION_MESSAGE =
  "No connection. Check your internet and try again.";

export const SIGN_IN_SERVER_FAULT_MESSAGE =
  "Something went wrong on our side. Try again in a minute.";

// The platform's answer for a wrong email or password (admin.controller WRONG_LOGIN).
const WRONG_LOGIN_TEXT = "incorrect login credentials.";

/**
 * What the admin reads when the sign-in request failed. "Don't match" is said only when the server really said
 * so: a 401, a 404, or the exact wrong-login 400. The platform also answers 400 for a validation failure and for
 * any crash in the login handler (a database blip), and a server fault must never tell the owner his password is wrong.
 */
export const signInFailureMessage = (errors) => {
  // No real answer (offline, DNS, blocked, timed out): never blame the server, and never retry-wording a fault.
  if (!hasServerAnswer(errors)) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    return offline ? SIGN_IN_NO_CONNECTION_MESSAGE : SIGN_IN_UNREACHABLE_MESSAGE;
  }
  const response = errors.response;
  const { status } = response;
  const serverText = typeof response.data?.message === "string" ? response.data.message : "";
  if (status === 401 || status === 404) return SIGN_IN_BAD_CREDENTIALS_MESSAGE;
  if (status === 400) {
    return serverText.trim().toLowerCase() === WRONG_LOGIN_TEXT
      ? SIGN_IN_BAD_CREDENTIALS_MESSAGE
      : SIGN_IN_SERVER_FAULT_MESSAGE;
  }
  if (status >= 500) return SIGN_IN_SERVER_FAULT_MESSAGE;
  return serverText || SIGN_IN_SERVER_FAULT_MESSAGE;
};

function useAuth() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const query = useQuery();

  const { token, signedIn } = useSelector((state) => state.auth.session);

  const signIn = async (values) => {
    let resp;
    try {
      resp = await apiSignIn(values);
    } catch (errors) {
      return {
        status: "failed",
        message: signInFailureMessage(errors),
      };
    }

    try {
      if (resp?.data?.token) {
        dispatch(onSignInSuccess(resp));
        const adminRole = resp?.data?.role || 'super_admin';
        dispatch(
          setUser({
            ...resp?.data,
            appConfig: [
              {
                ...resp.data?.appConfig?.[0],
                businessLogo:
                  resp.data?.appConfig?.[0]?.businessLogo?.[0]?.original || "",
              },
            ],
            authority: [adminRole],
            role: adminRole,
          })
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(safeRedirectPath(redirectUrl, appConfig.authenticatedEntryPath));
        return {
          status: "success",
          message: "signed in",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }

    return {
      status: "failed",
      message: "Sign in did not finish. Please try again.",
    };
  };

  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data) {
        const { token } = resp?.data?.token;
        dispatch(onSignInSuccess(token));
        if (resp.data.user) {
          dispatch(
            setUser(
              resp.data.user || {
                avatar: "",
                userName: "Anonymous",
                authority: ["USER"],
                email: "",
              }
            )
          );
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(safeRedirectPath(redirectUrl, appConfig.authenticatedEntryPath));
        return {
          status: "success",
          message: "",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    resetHomeDecisions();
    dispatch(setUser(initialState));
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    await apiSignOut({ deviceId: "321" });
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
  };
}

export default useAuth;
